import { lazy } from 'react';

import type {
  IntegrationDefinition,
  IntegrationLoaderContext,
  IntegrationMeta,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import type { QueryClient } from '@mint-lib/query-client';

import { adFormOAuthFlow } from './oauth.js';
import {
  ADFORM_URN,
  adFormConnectionRepository,
  fetchAdFormConnections,
} from './repository.js';

/** AdForm has OAuth and is a two-way ad-server — a two-way `adserver`, no Google policy notice, no ad-accounts block. */
export const adFormIntegrationMeta: IntegrationMeta = {
  urn: ADFORM_URN,
  displayName: 'AdForm',
  i18nNamespace: '@mint-lib/adform-integration',
  channel: 'adserver',
  direction: 'two-way',
};

/**
 * Assembles AdForm's `IntegrationDefinition`. `AddForm`/`ManageForm` are
 * loaded through the package's own published subpaths (never a relative
 * `./add-form` import) so this module — and anything that imports
 * `@mint-lib/adform-integration`'s root — never pulls the form UI into its
 * chunk; only `React.lazy()` touches it, on demand.
 */
export function createAdFormIntegration(): IntegrationDefinition {
  return {
    meta: adFormIntegrationMeta,
    connection: {
      AddForm: lazy(() => import('@mint-lib/adform-integration/add-form')),
      ManageForm: lazy(
        () => import('@mint-lib/adform-integration/manage-form'),
      ),
      repository: adFormConnectionRepository,
      oauth: adFormOAuthFlow,
      async prefetch(
        ctx: IntegrationLoaderContext,
      ): Promise<{ connections?: NormalizedConnection[] } | void> {
        const connections = await fetchAdFormConnections(
          ctx.queryClient as QueryClient,
        );
        return { connections };
      },
    },
  };
}
